import React, { FunctionComponent } from "react";
import { Grid, Skeleton, Typography } from "@mui/material";
import { AProgressTotalizer } from "@atiautomacao/ati-ui-library";
import Battery3BarOutlinedIcon from "@mui/icons-material/Battery3BarOutlined";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import ImportExportOutlinedIcon from "@mui/icons-material/ImportExportOutlined";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import { useBessEquipmentTotalizers } from "./BessTotalizersReactQuery";
import { BessEquipmentTotalizers } from "./BessTotalizersTypes";
import {
    computePowerScalePercent,
    formatEnergy,
    formatSignedPower,
    formatSoc,
    unitOf,
} from "./BessDetailTotalizersUtils";

const ERROR_MESSAGE = "Não foi possível carregar os totalizadores do BESS.";
const CARD_SX = { minHeight: 72 };
const TILE_BREAKPOINTS = { xs: 12, sm: 6, md: 3 } as const;

const LoadingSkeleton: FunctionComponent = () => (
    <Grid container spacing={2} data-testid="bess-detail-totalizers-loading">
        {Array.from({ length: 4 }).map((_, index) => (
            <Grid item {...TILE_BREAKPOINTS} key={index.valueOf()}>
                <Skeleton animation="wave" height={72} variant="rounded" width="100%" />
            </Grid>
        ))}
    </Grid>
);

const SocTile: FunctionComponent<{ data: BessEquipmentTotalizers }> = ({ data }) => (
    <AProgressTotalizer
        label="Estado de carga (SOC)"
        value={formatSoc(data.soc)}
        unit={unitOf(data.soc, data.socUnity ?? "%")}
        icon={Battery3BarOutlinedIcon}
        progressVariant="linear"
        progressValue={data.soc ?? undefined}
        showPercentage={false}
        progressOffsetY={"12px"}
        sx={CARD_SX}
    />
);

const ActivePowerTile: FunctionComponent<{ data: BessEquipmentTotalizers }> = ({ data }) => (
    <AProgressTotalizer
        label="Potência ativa"
        value={formatSignedPower(data.activePower)}
        unit={unitOf(data.activePower, data.activePowerUnity ?? "kW")}
        icon={EnergySavingsLeafOutlinedIcon}
        progressVariant="bipolar"
        progressValue={computePowerScalePercent(data.activePower, data.powerScaleMin, data.powerScaleMax)}
        progressWidth="50%"
        progressOffsetY={"10px"}
        showPercentageSign
        sx={CARD_SX}
    />
);

const BessDetailTotalizers: FunctionComponent<{ equipmentId: number }> = ({ equipmentId }) => {
    const { data: response, isLoading } = useBessEquipmentTotalizers(equipmentId);
    const data = response?.data;

    if (isLoading) {
        return <LoadingSkeleton />;
    }

    // RN3b: num refresh automático que falha o react-query mantém o cache, então basta checar a
    // ausência de dado. Ramificar por `isError` desmontaria os totalizadores a cada blip da API.
    if (!data) {
        return (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
                {ERROR_MESSAGE}
            </Typography>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item {...TILE_BREAKPOINTS}>
                <SocTile data={data} />
            </Grid>
            <Grid item {...TILE_BREAKPOINTS}>
                <ActivePowerTile data={data} />
            </Grid>
            <Grid item {...TILE_BREAKPOINTS}>
                <AProgressTotalizer
                    label="Energia exportada"
                    value={formatEnergy(data.exportedEnergy)}
                    unit={unitOf(data.exportedEnergy, data.exportedEnergyUnity)}
                    icon={ImportExportOutlinedIcon}
                    sx={CARD_SX}
                />
            </Grid>
            <Grid item {...TILE_BREAKPOINTS}>
                <AProgressTotalizer
                    label="Energia importada"
                    value={formatEnergy(data.importedEnergy)}
                    unit={unitOf(data.importedEnergy, data.importedEnergyUnity)}
                    icon={SystemUpdateAltOutlinedIcon}
                    sx={CARD_SX}
                />
            </Grid>
        </Grid>
    );
};

export default BessDetailTotalizers;
